/**
 * Main application entry point
 */

import { Gallery } from './gallery';
import { profile, ProfileLink } from './profileData';
import { themeManager } from './theme';
import { CLOSE_SVG } from './icons';
import './styles/main.css';

// The "ResizeObserver loop completed with undelivered notifications" message
// is a benign browser warning (not a real error) that can otherwise surface
// as an uncaught runtime error overlay. Silence just that specific message.
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);

class PortfolioApp {
  private static readonly SUN_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/></svg>';
  private static readonly MOON_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>';
  private static readonly MENU_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>';

  /**
   * A regular dodecahedron oriented so one pentagonal face rests flat on
   * the "ground" and the opposite pentagonal face is tipped toward the
   * viewer, plus a slight spin around the vertical axis so no vertex or
   * edge sits exactly at the bottom middle (matching the classic "resting"
   * dodecahedron illustration without its perfect left/right symmetry).
   * Orthographically projected and flat-shaded in 6 tones sampled from the
   * theme gradient (a 50/50 mix of black and var(--color-primary) ->
   * var(--color-secondary), which darkens/desaturates the primary end so
   * shaded faces read as shadow rather than a bright highlight), lit
   * from the upper-left. Geometry/lighting is precomputed (the top face
   * plus the 5 side faces facing the camera are the only ones visible from
   * this angle; regenerate with scripts/gen-dodecahedron-svg.js if the
   * orientation needs to change); only the fill tones react live to the
   * theme.
   */
  private static readonly AVATAR_DODECAHEDRON_SVG = `
    <svg viewBox="0 0 100 100" class="avatar-dodecahedron" aria-hidden="true">
      <g transform="translate(50,50)">
        <path d="M -26.81,29.76 L -8.29,43.28 L -13.41,28.89 L -35.10,6.49 L -43.38,7.03 Z" style="fill: color-mix(in srgb, var(--color-secondary) 20%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 80%); stroke: color-mix(in srgb, var(--color-secondary) 20%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 80%); stroke-width: 0.9; stroke-linejoin: round" />
        <path d="M -35.10,-20.54 L -43.38,7.03 L -35.10,6.49 L -21.69,-21.41 L -21.69,-38.12 Z" style="fill: color-mix(in srgb, var(--color-secondary) 80%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 20%); stroke: color-mix(in srgb, var(--color-secondary) 80%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 20%); stroke-width: 0.9; stroke-linejoin: round" />
        <path d="M -13.41,28.89 L -8.29,43.28 L 21.69,38.12 L 35.10,20.54 L 13.41,14.84 Z" style="fill: color-mix(in srgb, var(--color-secondary) 0%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 100%); stroke: color-mix(in srgb, var(--color-secondary) 0%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 100%); stroke-width: 0.9; stroke-linejoin: round" />
        <path d="M -21.69,-21.41 L 8.29,-16.25 L 26.81,-29.76 L 8.29,-43.28 L -21.69,-38.12 Z" style="fill: color-mix(in srgb, var(--color-secondary) 100%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 0%); stroke: color-mix(in srgb, var(--color-secondary) 100%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 0%); stroke-width: 0.9; stroke-linejoin: round" />
        <path d="M 8.29,-16.25 L 13.41,14.84 L 35.10,20.54 L 43.38,-7.03 L 26.81,-29.76 Z" style="fill: color-mix(in srgb, var(--color-secondary) 40%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 60%); stroke: color-mix(in srgb, var(--color-secondary) 40%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 60%); stroke-width: 0.9; stroke-linejoin: round" />
        <path d="M -35.10,6.49 L -13.41,28.89 L 13.41,14.84 L 8.29,-16.25 L -21.69,-21.41 Z" style="fill: color-mix(in srgb, var(--color-secondary) 60%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 40%); stroke: color-mix(in srgb, var(--color-secondary) 60%, color-mix(in srgb, var(--color-primary) 50%, black 50%) 40%); stroke-width: 0.9; stroke-linejoin: round" />
      </g>
    </svg>`.trim();

  private static readonly LINK_ICONS: Record<ProfileLink['icon'], string> = {
    github: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="social-icon" d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" fill-rule="evenodd" clip-rule="evenodd"/></svg>',
    linkedin: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="social-icon" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
    email: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="2"><rect class="social-icon" x="2" y="4" width="20" height="16" rx="2" fill="none"/><path d="m2 6 10 7 10-7" fill="none"/></svg>',
  };

  constructor() {
    this.renderSidebar();
    new Gallery();
    this.setupThemeToggle();
    this.setupMobileNavToggle();
    this.setupMobileOverscrollGuard();
  }

  /**
   * Preserve normal page scrolling (so mobile browser chrome can collapse),
   * while suppressing viewport rubber-band overscroll at the page edges on
   * touch devices. This guards both top and bottom bounce.
   */
  private setupMobileOverscrollGuard(): void {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isCoarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const EDGE_EPSILON_PX = 1;

    if (!(isTouchDevice && isCoarsePointer)) return;

    let startY = 0;
    let tracking = false;

    const hasScrollableAncestor = (startEl: EventTarget | null, deltaY: number): boolean => {
      let el = startEl instanceof HTMLElement ? startEl : null;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);
        if (canScrollY && el.scrollHeight > el.clientHeight + 1) {
          const canScrollFurtherDown = deltaY < 0 && (el.scrollTop + el.clientHeight) < (el.scrollHeight - EDGE_EPSILON_PX);
          const canScrollFurtherUp = deltaY > 0 && el.scrollTop > 0;
          if (canScrollFurtherDown || canScrollFurtherUp) return true;
        }
        el = el.parentElement;
      }
      return false;
    };

    window.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });

    window.addEventListener('touchend', () => {
      tracking = false;
    }, { passive: true });

    window.addEventListener('touchcancel', () => {
      tracking = false;
    }, { passive: true });

    window.addEventListener('touchmove', (e) => {
      if (!tracking || e.touches.length !== 1) return;

      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      if (hasScrollableAncestor(e.target, deltaY)) return;

      const scroller = document.scrollingElement || document.documentElement;
  const atTop = scroller.scrollTop <= EDGE_EPSILON_PX;
  const atBottom = scroller.scrollTop + window.innerHeight >= scroller.scrollHeight - EDGE_EPSILON_PX;
      const pullingPastTop = deltaY > 0 && atTop;
      const pullingPastBottom = deltaY < 0 && atBottom;

      if (pullingPastTop || pullingPastBottom) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  private renderSidebar(): void {
    const avatarEl = document.getElementById('sidebar-avatar');
    const nameEl = document.getElementById('sidebar-name');
    const roleEl = document.getElementById('sidebar-role');
    const bioEl = document.getElementById('sidebar-bio');
    const linksEl = document.getElementById('sidebar-links');

    if (avatarEl) {
      if (profile.avatarSrc) {
        avatarEl.style.backgroundImage = `url("${profile.avatarSrc}")`;
        avatarEl.style.backgroundSize = 'cover';
        avatarEl.style.backgroundPosition = 'center';
      } else {
        avatarEl.innerHTML = PortfolioApp.AVATAR_DODECAHEDRON_SVG;
      }
    }

    if (nameEl) nameEl.textContent = profile.name;
    if (roleEl) roleEl.textContent = profile.role;
    if (bioEl) bioEl.textContent = profile.bio;

    if (linksEl) {
      for (const link of profile.links) {
        const a = document.createElement('a');
        a.href = link.href;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.className = 'sidebar-link';
        a.setAttribute('aria-label', link.label);
        a.innerHTML = `${PortfolioApp.LINK_ICONS[link.icon]}<span>${link.label}</span>`;
        linksEl.appendChild(a);
      }
    }
  }

  private setupThemeToggle(): void {
    const themeBtn = document.getElementById('theme-btn');
    if (!themeBtn) return;

    this.updateThemeButton(themeBtn);
    themeBtn.addEventListener('click', () => {
      themeManager.toggleTheme();
      this.updateThemeButton(themeBtn);
    });
  }

  private updateThemeButton(themeBtn: HTMLElement): void {
    const isDark = themeManager.getTheme() === 'dark';
    themeBtn.innerHTML = isDark ? PortfolioApp.SUN_SVG : PortfolioApp.MOON_SVG;
    themeBtn.title = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  }

  /**
   * On narrow viewports the category tabs are hidden behind a floating menu
   * button (styled to match the theme toggle); pressing it expands the
   * category nav into a full-width, blurred, vertically-stacked panel.
   */
  private setupMobileNavToggle(): void {
    const navBtn = document.getElementById('mobile-nav-btn');
    const nav = document.getElementById('category-nav');
    if (!navBtn || !nav) return;

    navBtn.innerHTML = PortfolioApp.MENU_SVG;

    const setOpen = (open: boolean) => {
      document.body.classList.toggle('mobile-nav-open', open);
      navBtn.setAttribute('aria-expanded', String(open));
      navBtn.innerHTML = open ? CLOSE_SVG : PortfolioApp.MENU_SVG;
      navBtn.title = open ? 'Close categories' : 'Toggle categories';
    };

    navBtn.addEventListener('click', () => {
      setOpen(!document.body.classList.contains('mobile-nav-open'));
    });

    // Selecting a category should close the mobile menu.
    nav.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.category-tab')) setOpen(false);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new PortfolioApp();
});
